/**
 * Markdown and HTML rendering utilities for AG-UI Chat.
 *
 * Provides safe HTML rendering with support for:
 * - Allowed HTML tags (post-sanitize allowlist)
 * - Markdown syntax including:
 *   - Headings
 *   - Lists (ordered/unordered)
 *   - Task lists
 *   - Blockquotes
 *   - Tables
 *   - Horizontal rules
 *   - Inline code + fenced code blocks
 *   - Links
 *
 * Security:
 * - Markdown is rendered to HTML and then sanitized with a strict allowlist.
 * - Links are forced to open in a new tab with rel="noopener noreferrer".
 */

import MarkdownIt from 'markdown-it';
import taskLists from 'markdown-it-task-lists';
import DOMPurify from 'dompurify';

/**
 * Escapes HTML special characters to prevent XSS.
 * (Kept for backward compatibility / other potential usage.)
 */
export function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// Create the parser once (important for performance during streaming updates)
const md = new MarkdownIt({
  /**
   * Keep html enabled for backwards compatibility with previously-allowed tags,
   * but do NOT trust it—DOMPurify will sanitize output.
   */
  html: true,
  linkify: true,
  breaks: true, // keeps existing behavior: \n => <br>
})
  .use(taskLists, {
    enabled: true,
    label: true,
    labelAfter: true,
  });

/**
 * Custom DOMPurify hook to ensure checkbox inputs retain their type attribute.
 * This runs after sanitization to fix any stripped attributes.
 */
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  // Fix checkbox inputs that lost their type attribute
  if (node.tagName === 'INPUT' && node.classList.contains('task-list-item-checkbox')) {
    node.setAttribute('type', 'checkbox');
    node.setAttribute('disabled', 'disabled');
  }
});

// Allowlist for what we want to render inside chat bubbles.
const ALLOWED_TAGS = [
  // Inline / basic
  'a',
  'p',
  'br',
  'strong',
  'em',
  'code',
  'pre',

  // Requested by issue: headings
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',

  // Requested by issue: lists
  'ul',
  'ol',
  'li',

  // Requested by issue: blockquotes
  'blockquote',

  // Requested by issue: tables
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',

  // Requested by issue: horizontal rules
  'hr',

  // Task list plugin output
  'input',
  'label',

  // Used for your external link icon span
  'span',
];

const ALLOWED_ATTR = [
  // links
  'href',
  'target',
  'rel',

  // classes (for icon and any future code highlighting hooks)
  'class',

  // task lists
  'type',
  'checked',
  'disabled',
  'id',
  'for',

  // tables
  'colspan',
  'rowspan',
  'align',
];

/**
 * Renders markdown safely into sanitized HTML.
 */
export function renderMarkdown(text: string): string {
  if (!text) return '';

  // 1) Render markdown to HTML
  const renderedHtml = md.render(text);

  // 2) Sanitize HTML (strict allowlist)
  const cleanHtml = DOMPurify.sanitize(renderedHtml, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    /**
     * Allow all data-* attributes for custom functionality.
     */
    ALLOW_DATA_ATTR: true,
    /**
     * Allow https/http/mailto/tel plus relative links and anchors.
     * Blocks javascript: and other unsafe protocols.
     */
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|\/|#)/i,
    /**
     * Hook to preserve type="checkbox" on input elements.
     * DOMPurify may strip certain attributes; this ensures checkboxes render correctly.
     */
    RETURN_DOM_FRAGMENT: false,
  });

  // 2b) Fix malformed checkbox HTML from markdown-it-task-lists (missing space before type)
  // The plugin outputs `"type="checkbox"` (missing space), so we fix it before DOM parsing
  const fixedHtml = cleanHtml.replace(
    /"type="checkbox"/g,
    '" type="checkbox"',
  );

  // 3) Post-process: enforce link safety + add icon without duplication
  const template = document.createElement('template');
  template.innerHTML = fixedHtml;

  // Ensure task-list checkboxes have type="checkbox" and are disabled (read-only UI)
  // DOMPurify may strip type attribute, so we re-add it via DOM manipulation
  template.content
    .querySelectorAll<HTMLInputElement>('input.task-list-item-checkbox')
    .forEach((inp) => {
      inp.setAttribute('type', 'checkbox');
      inp.setAttribute('disabled', '');
    });

  template.content.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((a) => {
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');

    // Avoid duplicating icon on repeated renders (streaming updates)
    a.querySelectorAll('.agui-chat__link-icon').forEach((el) => el.remove());

    const icon = document.createElement('span');
    icon.className = 'agui-chat__link-icon';
    icon.textContent = '↗';

    a.appendChild(document.createTextNode(' '));
    a.appendChild(icon);
  });

  return template.innerHTML;
}

/**
 * Formats a tool/function name into a human-readable string.
 *
 * Converts snake_case or camelCase to Title Case with spaces.
 * Example: "search_articles" -> "Search articles"
 * Example: "getWeatherData" -> "Get weather data"
 */
export function formatToolName(toolName: string): string {
  return toolName
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}
